import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertOctagon, AlertTriangle, Bot, Eye, Play, Plus, RefreshCw, Shield } from 'lucide-react';
import type { DaemonRecord } from '../../generated/api/types.gen.js';
import { listCatalogVersionAdoptionsOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import {
  Badge,
  Button,
  Callout,
  ConfirmDialog,
  DataTable,
  EmptyState,
  MultiSelectDropdown,
  Toolbar,
  useToast,
} from '../../components/ui/index.js';
import type { BadgeVariant, DataTableColumn } from '../../components/ui/index.js';
import { ListPage } from '../../components/page/ListPage.js';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import { EntitySearchInput } from '../../components/entity/EntitySearchInput.js';
import { CatalogAdoptionStatusPanel } from '../../components/catalog/CatalogAdoptionStatusPanel.js';
import { Can } from '../../components/auth/Can.js';
import { PAGE_HELP } from '../../help/index.js';
import type { EntitySearchResult } from '../../hooks/useEntitySearch.js';
import { daemonKeys, daemonReads, daemonWrites } from './daemon/daemon-queries.js';
import type { DaemonScope } from './daemon/daemon-queries.js';
import { buildDaemonScope } from './daemon/DaemonScopeContext.js';
import { humanize } from './admin-format.js';

const STATUS_VARIANTS: Record<string, BadgeVariant> = {
  idle: 'neutral',
  running: 'success',
  paused: 'warning',
  error: 'danger',
  disabled: 'neutral',
};

function countOptions(daemons: DaemonRecord[], pick: (d: DaemonRecord) => string) {
  const counts = new Map<string, number>();
  for (const d of daemons) counts.set(pick(d), (counts.get(pick(d)) ?? 0) + 1);
  return Array.from(counts.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([value, count]) => ({ value, label: humanize(value), count }));
}

interface DaemonsPageProps {
  scope?: DaemonScope;
}

export const DaemonsPage: React.FC<DaemonsPageProps> = ({ scope = 'org' }) => {
  const help = PAGE_HELP.daemons;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { notify } = useToast();
  const { title, basePath, entityLabel, entityLabelPlural, policy } = buildDaemonScope(scope);
  const reads = daemonReads(scope);
  const writes = daemonWrites(scope);
  const [depts, setDepts] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [confirmKillAll, setConfirmKillAll] = useState(false);

  const listQuery = useQuery(reads.list());
  const summaryQuery = useQuery(reads.summary());
  const adoptionsQuery = useQuery(listCatalogVersionAdoptionsOptions());
  const summary = summaryQuery.data;

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: daemonKeys(scope).list() }),
      queryClient.invalidateQueries({ queryKey: daemonKeys(scope).summary() }),
    ]);

  const killAll = useMutation({
    ...writes.killAll(),
    onSuccess: async () => {
      setConfirmKillAll(false);
      notify(`Every ${entityLabel} was turned off`);
      await refresh();
    },
    onError: () => notify(`Could not turn off the ${entityLabelPlural}`, 'danger'),
  });
  const resumeAll = useMutation({
    ...writes.resumeAll(),
    onSuccess: async () => {
      notify(`Turned the ${entityLabelPlural} back on`);
      await refresh();
    },
    onError: () => notify(`Could not turn the ${entityLabelPlural} back on`, 'danger'),
  });

  const allDaemons = listQuery.data?.data ?? [];
  const adoptions = (adoptionsQuery.data?.data ?? []).filter((adoption) => adoption.ownerType === 'daemon');

  const daemons = allDaemons.filter(
    (d) =>
      (depts.length === 0 || depts.includes(d.department ?? 'custom')) &&
      (types.length === 0 || types.includes(d.type ?? 'operative')) &&
      (statuses.length === 0 || statuses.includes(d.status ?? 'idle')),
  );

  const addLabel = scope === 'system' ? 'New daemon' : 'Add to workforce';
  const addLink = (
    <Can policy={policy}>
      <Link to={`${basePath}/new`} className={buttonStyles({ variant: 'primary', appearance: 'fill', size: 'sm' })}>
        <Plus size={14} aria-hidden="true" />
        {addLabel}
      </Link>
    </Can>
  );

  const columns: DataTableColumn<DaemonRecord>[] = [
    {
      key: 'name',
      header: entityLabel.charAt(0).toUpperCase() + entityLabel.slice(1),
      sortable: true,
      sortValue: (d) => (d.name ?? '').toLowerCase(),
      render: (d) => (
        <div className="min-w-0 max-w-sm">
          <Link
            to={`${basePath}/${d.id}`}
            className="block truncate text-sm font-medium text-heading hover:text-accent-text"
            onClick={(e) => e.stopPropagation()}
          >
            {d.name ?? `${humanize(entityLabel)} ${d.id}`}
          </Link>
          {(d.mandate || d.description) && (
            <span className="block truncate text-xs text-muted">{d.mandate || d.description}</span>
          )}
          {adoptions.some((a) => a.ownerId === Number(d.id) && a.status === 'stale') && (
            <span className="block text-xs text-warning">Library update available</span>
          )}
        </div>
      ),
    },
    {
      key: 'department',
      header: 'Department',
      render: (d) => <Badge variant="neutral">{humanize(d.department ?? 'custom')}</Badge>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (d) => (
        <div className="flex items-center gap-1.5">
          <Badge variant={STATUS_VARIANTS[d.status ?? ''] ?? 'neutral'} dot>
            {humanize(d.status ?? 'idle')}
          </Badge>
          {d.probationUntil && new Date(d.probationUntil) > new Date() && <Badge variant="warning">On probation</Badge>}
        </div>
      ),
    },
    {
      key: 'supervisor',
      header: 'Supervisor',
      render: (d) => (
        <span className="text-sm text-muted">
          {d.supervisorDaemonId
            ? (allDaemons.find((s) => s.id === d.supervisorDaemonId)?.name ?? 'Unknown')
            : 'Human review'}
        </span>
      ),
    },
    {
      key: 'lastRunAt',
      header: 'Last run',
      sortable: true,
      sortValue: (d) => d.lastRunAt,
      render: (d) => (
        <span className="whitespace-nowrap text-sm text-muted">
          {d.lastRunAt ? new Date(d.lastRunAt).toLocaleString() : 'Never'}
        </span>
      ),
    },
    {
      key: 'quality',
      header: 'Quality',
      align: 'right',
      sortable: true,
      sortValue: (d) => (d.qualityScore ? Number.parseFloat(d.qualityScore) : null),
      render: (d) => (
        <span className="text-sm text-body">
          {d.qualityScore ? `${(Number.parseFloat(d.qualityScore) * 100).toFixed(0)}%` : 'None yet'}
        </span>
      ),
    },
  ];

  const resumeButton = (
    <Button variant="success" size="sm" loading={resumeAll.isPending} onClick={() => resumeAll.mutate({})}>
      <RefreshCw size={14} aria-hidden="true" />
      Resume all
    </Button>
  );

  return (
    <ListPage
      title={title}
      subtitle={
        scope === 'system'
          ? 'Autonomous daemons that watch over the platform.'
          : 'Your AI employees: each has a mandate, a department and a supervisor.'
      }
      helpContent={help}
      actions={
        <Can policy={policy}>
          {summary && !summary.globallyEnabled ? (
            resumeButton
          ) : (
            <Button variant="danger" size="sm" onClick={() => setConfirmKillAll(true)}>
              <AlertOctagon size={14} aria-hidden="true" />
              {scope === 'system' ? 'Kill all' : 'Terminate all'}
            </Button>
          )}
          {addLink}
        </Can>
      }
      stats={
        summary
          ? [
              { label: scope === 'system' ? 'Daemons' : 'Employees', value: summary.totalDaemons, icon: Bot },
              { label: 'Enabled', value: summary.enabledDaemons, icon: Play },
              { label: 'Findings, 30 days', value: summary.recentFindings, icon: AlertTriangle },
              {
                label: 'Pending reviews',
                value: summary.pendingReviews,
                icon: Eye,
                severity: summary.pendingReviews > 0 ? 'warning' : undefined,
              },
              { label: 'Active grants', value: summary.permissionGrantUsage.active, icon: Shield },
              { label: 'Grant uses, 7 days', value: summary.permissionGrantUsage.usedLast7d, icon: Shield },
            ]
          : undefined
      }
      alert={
        <>
          {(listQuery.isError || summaryQuery.isError) && (
            <Callout
              tone="danger"
              icon={AlertTriangle}
              title={`Could not load the ${entityLabelPlural}`}
              actions={
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    void listQuery.refetch();
                    void summaryQuery.refetch();
                  }}
                >
                  Retry
                </Button>
              }
            >
              Check your connection and try again.
            </Callout>
          )}
          {summary && !summary.globallyEnabled && (
            <Callout tone="danger" icon={AlertOctagon} title="The global kill switch is on">
              Every {entityLabel} is turned off. Use Resume all to turn them back on.
            </Callout>
          )}
          <CatalogAdoptionStatusPanel
            adoptions={adoptions}
            loading={adoptionsQuery.isPending}
            title={scope === 'system' ? 'Daemon library updates' : 'Workforce library updates'}
            maxVisible={5}
          />
        </>
      }
      toolbar={
        <Toolbar
          search={
            <EntitySearchInput
              entityType="employee"
              workforceScope={scope}
              placeholder={`Search ${entityLabelPlural} by name, mandate or department…`}
              onSelect={(row: EntitySearchResult) => navigate(`${basePath}/${row.id}`)}
            />
          }
          right={
            <>
              <MultiSelectDropdown
                label="Department"
                options={countOptions(allDaemons, (d) => d.department ?? 'custom')}
                values={depts}
                onChange={setDepts}
              />
              <MultiSelectDropdown
                label="Type"
                options={countOptions(allDaemons, (d) => d.type ?? 'operative')}
                values={types}
                onChange={setTypes}
              />
              <MultiSelectDropdown
                label="Status"
                options={countOptions(allDaemons, (d) => d.status ?? 'idle')}
                values={statuses}
                onChange={setStatuses}
              />
            </>
          }
        />
      }
    >
      {!listQuery.isError && (
        <DataTable
          columns={columns}
          rows={daemons}
          rowKey={(d) => d.id}
          onRowClick={(d) => navigate(`${basePath}/${d.id}`)}
          loading={listQuery.isPending}
          aria-label={title}
          empty={
            <EmptyState
              icon={Bot}
              title={help.emptyState.title}
              description={help.emptyState.description}
              learnItems={help.emptyState.learnItems}
              action={addLink}
            />
          }
        />
      )}
      <ConfirmDialog
        open={confirmKillAll}
        onClose={() => setConfirmKillAll(false)}
        onConfirm={() => killAll.mutate({})}
        title={scope === 'system' ? 'Kill all daemons' : 'Terminate all employees'}
        message={`Turn off every ${entityLabel}? Nothing runs until you resume them.`}
        confirmLabel={scope === 'system' ? 'Kill all' : 'Terminate all'}
        loading={killAll.isPending}
      />
    </ListPage>
  );
};
